// Responsible for aggregating compatibility requests to reduce
// the number of network requests when multiple compatibility checks
// are present on the page

import log from 'salvo-lite/log'
import { garageService, garageServiceReady } from './garageService'
import _ from 'lodash-es'

const TAG = 'compatibilityService'

const MAX_PRODUCT_QUERY_SIZE = 100
export const UNKNOWN_COMPATIBILITY_RESULT = {
    definitelyCompatible: false,
    possiblyCompatible: false,
    possiblyIncompatible: false,
    definitelyIncompatible: false,
    compatibleCodes: [],
    incompatibleCodes: [],
    cached: false
}
const CHECK_DEBOUNCE_TIME_MS = 500
const CHECK_DEBOUNCE_MAX_MS = 5000

export class CompatibilityService {
    constructor () {
        this._queuedChecks = {} // {[productId: string]: CompatiblityCheckOneOff[]}
        this._subscriptionChecks = {} // {[productId: string]: CompatibilityCheckSubscription[]}

        this._checkDebounceFn = _.debounce(() => this._checkDebounceTriggered(), CHECK_DEBOUNCE_TIME_MS, {
            leading: false,
            trailing: true,
            maxWait: CHECK_DEBOUNCE_MAX_MS
        })

        this._init()
    }

    async _init () {
        await garageServiceReady

        garageService.onActiveVehicleChange.subscribe((v) => {
            this._onVehicleChanged(v)
        })
    }

    get _allSubscriptionChecks () {
        let r = []
        for (let productId in this._subscriptionChecks) {
            for (let check of this._subscriptionChecks[productId]) {
                r.push(check)
            }
        }
        return r
    }

    async _onVehicleChanged (activeVehicle) {
        if (!activeVehicle) {
            // No vehicle, compatibility is unknown
            for (let check of this._allSubscriptionChecks) {
                check.dispatch(UNKNOWN_COMPATIBILITY_RESULT)
            }
            return
        }

        const allProductIds = Object.keys(this._subscriptionChecks)
        // Break subscribed IDs into chunks for API request
        for (let chunk = 0; chunk < allProductIds.length; chunk += MAX_PRODUCT_QUERY_SIZE) {
            const chunkProductIds = allProductIds.slice(chunk, chunk + MAX_PRODUCT_QUERY_SIZE)

            log.debug(TAG, 'Requesting subs', {
                chunkProductIds
            })
            garageService.checkCompatibility(chunkProductIds, activeVehicle).then((compatMap) => {
                this._updateSubscriptions(compatMap)
            }).catch((err) => {
                throw err
            })
        }
    }

    _updateSubscriptions (compatibilityMap) {
        for (let productId in compatibilityMap) {
            const compatibilityResult = compatibilityMap[productId]

            const subscriptions = this._subscriptionChecks[productId]
            if (subscriptions) {
                for (let sub of subscriptions) {
                    sub.dispatch(compatibilityResult)
                }
            }
        }
    }

    _debounceCheck () {
        this._checkDebounceFn()
    }
    async _checkDebounceTriggered () {
        await garageServiceReady

        log.debug(TAG, 'Running queued compat checks')

        // Time to perform a batch of checks
        const checks = this._queuedChecks
        this._queuedChecks = {}

        const activeVehicle = garageService.activeVehicle
        if (!activeVehicle) {
            // No vehicle, compatibility is unknown
            for (let productId in checks) {
                const productChecks = checks[productId]
                for (let check of productChecks) {
                    check.resolve(UNKNOWN_COMPATIBILITY_RESULT)
                }
            }
            log.debug(TAG, 'Compat checks resolved to unknown because no vehicle')
            return
        }

        const allProductIds = Object.keys(checks)
        // Break IDs into chunks for API request
        for (let chunk = 0; chunk < allProductIds.length; chunk += MAX_PRODUCT_QUERY_SIZE) {
            const chunkProductIds = allProductIds.slice(chunk, chunk + MAX_PRODUCT_QUERY_SIZE)

            log.debug(TAG, 'Requesting checks', {
                chunkProductIds
            })
            garageService.checkCompatibility(chunkProductIds, activeVehicle).then((compatMap) => {
                this._updateChecks(compatMap, checks)
            }).catch((err) => {
                throw err
            })
        }
    }
    _updateChecks (compatibilityMap, checks) {
        // Resolve checks that we got results for
        for (let productId in compatibilityMap) {
            const compatibilityResult = compatibilityMap[productId]

            const productChecks = checks[productId]
            if (productChecks) {
                for (let check of productChecks) {
                    check.resolve(compatibilityResult)
                }
            }
        }

        // Reject checks that didn't resolve
        for (let productId in checks) {
            if (compatibilityMap[productId]) {
                continue
            }

            const check = checks[productId]
            check.reject(new Error(`Didn't receive compatibility result - possibly unknown product?`))
        }
    }

    /**
     * Performs a one-off compatibility check.
     * One-off checks are throttled and batched.
     * @param {string} productId Shopify product ID
     * @returns {Promise<CompatibilityResult>} Compatibility result
     */
    checkCompatibility (productId) {
        if (productId.indexOf('gid://')) {
            // Convert to non-legacy format
            productId = `gid://shopify/Product/${productId}`
        }

        const check = new CompatibilityCheckOneOff(productId)
        if (!this._queuedChecks[productId]) {
            this._queuedChecks[productId] = []
        }
        this._queuedChecks[productId].push(check)

        this._debounceCheck()

        log.debug(TAG, 'Queued compat check', {
            productId,
            check
        })

        return check.promise
    }

    /**
     * Subscribe to changes in compatibility for a product.
     * @param {string} productId Shopify product ID
     * @param {((CompatibilityResult) => void)} callback Called when compatibility changes
     * @param {boolan} initialCheck Check & trigger the subscription immediately
     * @returns {(() => void)} Unsubscribe function which destroys subscription
     */
    subscribeCompatibility (productId, callback, initialCheck = true) {
        if (productId.indexOf('gid://')) {
            // Convert to non-legacy format
            productId = `gid://shopify/Product/${productId}`
        }

        const sub = new CompatibilityCheckSubscription(productId, callback)
        if (!this._subscriptionChecks[productId]) {
            this._subscriptionChecks[productId] = []
        }
        this._subscriptionChecks[productId].push(sub)

        if (initialCheck) {
            this.checkCompatibility(productId).then((result) => {
                sub.dispatch(result)
            })
        }

        const unsubFunction = () => {
            const index = this._subscriptionChecks[productId].indexOf(sub)
            if (index > -1) {
                this._subscriptionChecks[productId].splice(index, 1)
            }
        }

        return unsubFunction
    }
}

export const compatibilityService = new CompatibilityService()

class CompatibilityCheckOneOff {
    constructor (productId) {
        this._productId = productId
        this._promise = new Promise((resolve, reject) => {
            this._resolve = resolve
            this._reject = reject
        })
    }

    get productId () {
        return this._productId
    }

    get promise () {
        return this._promise
    }

    resolve (compatibilityResult) {
        this._resolve(compatibilityResult)
    }
    reject (err) {
        this._reject(err)
    }
}

class CompatibilityCheckSubscription {
    constructor (productId, callback) {
        this._productId = productId
        this._callback = callback
    }

    get productId () {
        return this._productId
    }

    dispatch (compatibilityResult) {
        this._callback(compatibilityResult)
    }
}



// WEBPACK FOOTER //
// ./src/resources/scripts/services/compatibilityService.js